/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { ReactComponentElement } from 'react';

// models and services
import CardService from '../../services/card.service';
import CashFlowService from '../../services/cashflow.service';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import BasicDrawer from './common/BasicDrawer';
import BottomButtons from '../../components/bottom-buttons/BottomButtons';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import CardTop from '../../components/card-top/CardTop';
import LinkedGoal from './common/LinkedGoal';
import SavingsGoal from './common/SavingsGoal';
import { Label } from '@americanexpress/dls-react';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import DatePickers from '../../components/date-pickers/DatePickers';

// icons and styles
import { ReactComponent as BabyIcon } from 'src/bw/goal/baby.svg';
import { ReactComponent as GraduationCapIcon } from 'src/bw/goal/graduation-cap.svg';
import { ReactComponent as InfoIcon } from '../../components/mentors/info.svg';
import { ReactComponent as JessyNeutral } from '../../components/mentors/jessy-neutral.svg';
import './ExpenseGoalDrawer.scss';

export default class ExpenseGoalDrawer extends BasicDrawer<
    any,
    {
        wantsSavingsGoal: boolean;
        costChanged: boolean;
    }
> {
    public override state = {
        wantsSavingsGoal: CardService.hasSavingsGoal(this.props),
        costChanged: false
    };

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;

        let subtitle: string = '';
        let startDateLabel: string = 'Planned start date';
        let endDateLabel: string = 'Planned end date';
        let tip: JSX.Element = null;
        let icon: JSX.Element = null;

        let savingsGoal: Boolean = false;

        switch (drawerProps.card.description) {
            case 'have a baby':
                subtitle = LiteralsService.getLiteral('ExpenseGoalDrawer.baby.subtitle');
                startDateLabel = LiteralsService.getLiteral('ExpenseGoalDrawer.baby.startDateLabel');
                endDateLabel = 'Your child is independent date';
                const tip1: string = LiteralsService.getLiteral('ExpenseGoalDrawer.baby.tip1');
                const tip2: string = LiteralsService.getLiteral('ExpenseGoalDrawer.baby.tip2');
                const usda: JSX.Element = (
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.usda.gov/media/blog/2017/01/13/cost-raising-child"
                    >
                        US Department of Agriculture
                    </a>
                );
                tip = (
                    <Typography variant={TypographyVariants.HEADING_XS} className="before24">
                        {tip1}
                        {usda}
                        {tip2}
                    </Typography>
                );
                icon = <BabyIcon className="icon" key="icon" />;
                savingsGoal = true;
                break;
            case 'child starts college':
                icon = <GraduationCapIcon className="icon" key="icon" />;
                break;
        }

        const info: JSX.Element = this.state.costChanged ? (
            <div key="info" className="info">
                <InfoIcon className="info-icon" />
                <Typography key="header" className="info-caption" variant={TypographyVariants.LEGAL_PRIMARY}>
                    Updating this value will also update your savings goal.
                </Typography>
            </div>
        ) : null;

        let jessy: JSX.Element = null;
        if (_.get(this.props, 'mentor.state') === 'updateGoal') {
            if (_.get(this.props, 'mentor.goal') === 'take a vacation') {
                drawerProps.card.startDate = drawerProps.card.endDate = LocalDate.now().plusMonths(2);

                jessy = (
                    <div className="jessy after24" key="jessy">
                        <div className="avatar">
                            <JessyNeutral />
                        </div>
                        <Typography variant={TypographyVariants.CAPTION} className="caption">
                            Now you have a chance to change the details about your goal.{' '}
                            <b>Let's update the cost of this vacation to $15,000.</b>
                        </Typography>
                    </div>
                );
            }
        }

        const bottomButtons: JSX.Element = (
            <div className="bottom-buttons-container">
                <BottomButtons
                    wantsSavingsGoal={this.state.wantsSavingsGoal}
                    drawerProps={drawerProps}
                    showLoader={this.props.showLoader}
                    handleBack={this.props.handleBack}
                    handleUpdateCard={this.handleUpdateCard.bind(this)}
                    handleCreateLinkedSavingsCard={this.props.handleCreateLinkedSavingsCard}
                    handleOpenCard={this.props.handleOpenCard}
                    hideLoader={this.props.hideLoader}
                    handleDeleteCard={this.props.handleDeleteCard}
                />
            </div>
        );

        return (
            <div className="expense-goal-drawer drawer card-drawer scenario-card-drawer">
                <CardTop
                    card={drawerProps.card}
                    icon={icon}
                    handleUpdateCard={this.props.handleUpdateCard}
                    handleChangeCard={this.props.handleChangeCard}
                />

                <Typography variant={TypographyVariants.SUBTEXT} className="intro after32 color60" key="intro">
                    {subtitle}
                </Typography>

                <LinkedGoal
                    drawerProps={this.props.drawerProps}
                    handleOpenLinkedCard={this.props.handleOpenLinkedCard}
                />

                <DatePickers
                    startDateLabel={startDateLabel}
                    endDateLabel={endDateLabel}
                    startDate={drawerProps.card.startDate}
                    endDate={drawerProps.card.endDate}
                    maxDate={CashFlowService.getLastCashFlow(this.props.user.cashFlow).endDate}
                    handleChangeDate={this.props.handleChangeCardDate.bind(this)}
                />

                <div key="amount" className="after24">
                    <Label htmlFor="amount">Expected expense</Label>
                    <BWCurrencyInput
                        className="amount"
                        defaultValue={0}
                        id="amount"
                        status=""
                        value={-drawerProps.card.attributes.totalAmount}
                        showFractions={false}
                        onChange={(value: number) => {
                            this.props.handleChangeCardAttribute(
                                drawerProps.card,
                                `-${value}`,
                                'totalAmount',
                                'currency'
                            );
                        }}
                    />
                </div>

                {savingsGoal && drawerProps.card.attributes.totalAmount ? (
                    <SavingsGoal
                        drawerProps={drawerProps}
                        user={this.props.user}
                        wantsSavingsGoal={this.state.wantsSavingsGoal}
                        handleWantsSavingsGoal={this.handleWantsSavingsGoal.bind(this)}
                        setDrawer={this.props.setDrawer}
                        handleOpenSignup={() => {
                            console.warn('Deprecated: handleOpenSignup (Large Purchase)');
                            this.props.handleOpenSignup();
                        }}
                    />
                ) : null}
                {info}
                {bottomButtons}

                {jessy}
                {tip}
            </div>
        );
    }

    private handleWantsSavingsGoal(checked: boolean): void {
        this.setState({ wantsSavingsGoal: checked });
    }

    private async handleUpdateCard(): Promise<void> {
        if (this.props.drawerProps.card.attributes.purchaseName) {
            this.props.drawerProps.card.nickname = this.props.drawerProps.card.nickname
                ? this.props.drawerProps.card.nickname
                : `Save for ${this.props.drawerProps.card.attributes.purchaseName}`;
        }
        await this.props.handleUpdateCard();
    }
}
